export type SplitMode = 1 | 2 | 3;

/**
 * State of split page navigation.
 * pages: array of 1-based page indices currently shown in each pane [pane1, pane2, ...]
 * activeTurnIndex: which pane index (0 to splitCount - 1) will be updated on the NEXT forward page turn.
 */
export interface SplitPageState {
  pages: number[];
  activeTurnIndex: number;
}

/**
 * Creates the initial split page state for a given split mode and total page count.
 * For splitMode = 1: pages = [1], activeTurnIndex = 0
 * For splitMode = 2: pages = [1, 2] (or clamped if maxPages < 2), activeTurnIndex = 0
 * For splitMode = 3: pages = [1, 2, 3] (or clamped if maxPages < 3), activeTurnIndex = 0
 */
export function getInitialSplitPageState(splitMode: SplitMode, maxPages: number = 1): SplitPageState {
  const safeMax = Math.max(1, maxPages);
  const pages: number[] = [];
  for (let i = 0; i < splitMode; i++) {
    pages.push(Math.min(i + 1, safeMax));
  }
  return {
    pages,
    activeTurnIndex: 0,
  };
}

/**
 * Calculates the next state when navigating forward (+1 step).
 * Alternating / round-robin single-column page turn:
 * In 2-split:
 *   [1, 2] (next pane 0) -> [3, 2] (next pane 1)
 *   [3, 2] (next pane 1) -> [3, 4] (next pane 0)
 *   [3, 4] (next pane 0) -> [5, 4] ...
 *
 * In 3-split:
 *   [1, 2, 3] (next pane 0) -> [4, 2, 3] (next pane 1)
 *   [4, 2, 3] (next pane 1) -> [4, 5, 3] (next pane 2)
 *   [4, 5, 3] (next pane 2) -> [4, 5, 6] (next pane 0)
 *   [4, 5, 6] (next pane 0) -> [7, 5, 6] ...
 *
 * In 1-split:
 *   [1] -> [2] -> [3]
 */
export function getNextSplitPageState(
  currentState: SplitPageState,
  splitMode: SplitMode,
  maxPages: number
): SplitPageState {
  const safeMax = Math.max(1, maxPages);
  const { pages, activeTurnIndex } = currentState;
  const currentPanePage = pages[activeTurnIndex] ?? 1;

  // Next page for the active pane advances by splitMode
  const targetPage = currentPanePage + splitMode;

  // If already at or beyond maxPages, check if any further advancing is possible
  if (currentPanePage >= safeMax) {
    return currentState;
  }

  const nextPageNumber = Math.min(targetPage, safeMax);
  const newPages = [...pages];
  newPages[activeTurnIndex] = nextPageNumber;

  const nextTurnIndex = (activeTurnIndex + 1) % splitMode;

  return {
    pages: newPages,
    activeTurnIndex: nextTurnIndex,
  };
}

/**
 * Calculates the previous state when navigating backward (-1 step).
 * Reverses the forward alternating step.
 */
export function getPrevSplitPageState(
  currentState: SplitPageState,
  splitMode: SplitMode,
  _maxPages: number = 1
): SplitPageState {
  const { pages, activeTurnIndex } = currentState;
  // The pane that was last updated is (activeTurnIndex - 1 + splitMode) % splitMode
  const targetPaneIndex = (activeTurnIndex - 1 + splitMode) % splitMode;
  const currentPanePage = pages[targetPaneIndex] ?? 1;

  // The base starting page for this pane is targetPaneIndex + 1
  const minPageForPane = targetPaneIndex + 1;

  if (currentPanePage <= minPageForPane) {
    // Cannot go back any further for this pane
    return currentState;
  }

  const prevPageNumber = Math.max(minPageForPane, currentPanePage - splitMode);
  const newPages = [...pages];
  newPages[targetPaneIndex] = prevPageNumber;

  return {
    pages: newPages,
    activeTurnIndex: targetPaneIndex,
  };
}

/**
 * Computes the effective split mode (panel count) based on user's preferred split mode
 * and whether earlier panels already reach the end of the song content.
 *
 * Boundary rules:
 * - If panel 1 already reaches the song end (content fits in 1 panel), downsize to 1 panel.
 * - If panel 2 reaches the song end, panel 3 is redundant/empty, downsize to 2 panels.
 * - Always clamped between 1 and preferredSplitMode.
 */
export function computeEffectiveSplitMode(
  preferredSplitMode: SplitMode,
  contentHeight: number,
  clientHeight: number,
  overlapHeight: number = 42
): SplitMode {
  if (preferredSplitMode <= 1 || clientHeight <= 0 || contentHeight <= 0) {
    return preferredSplitMode;
  }

  // Panel 1 reaches song end if the entire content fits within 1 panel
  if (contentHeight <= clientHeight) {
    return 1;
  }

  // If user requested at least 2 panels, check if panel 2 reaches the song end
  const step = Math.max(1, clientHeight - overlapHeight);
  const panel2Coverage = step + clientHeight;

  if (contentHeight <= panel2Coverage) {
    return Math.min(preferredSplitMode, 2) as SplitMode;
  }

  return preferredSplitMode;
}
